/**
 * PoiDetail — /places/detail/<city_slug>/<category>/<name-slug>
 *
 * Displays a discovered Point of Interest with:
 *   - Hero (Wikipedia image + gradient fallback)
 *   - Structured facts (address, coords, website, phone, operator, hours, accessibility)
 *   - Wikipedia summary paragraph
 *   - "Report closure / relocation / inaccuracy" community moderation
 *   - `EntityMessageBoard` discussion (kind="poi") for neighbourhood chatter
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Globe, Phone, Mail, Clock, Accessibility, Building2,
  Hospital, GraduationCap, Church, Trees, Users, Utensils, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_ICONS = {
  hospitals:  { Icon: Hospital,        tint: "#E11D48", label: "Hospital" },
  schools:    { Icon: GraduationCap,   tint: "#0284C7", label: "School" },
  colleges:   { Icon: GraduationCap,   tint: "#7C3AED", label: "College" },
  worship:    { Icon: Church,          tint: "#B45309", label: "Place of Worship" },
  parks:      { Icon: Trees,           tint: "#059669", label: "Park" },
  charities:  { Icon: Users,           tint: "#DB2777", label: "Charity" },
  food_banks: { Icon: Utensils,        tint: "#EA580C", label: "Food Bank" },
};

export default function PoiDetail() {
  const { "*": rest } = useParams();
  const poiSlug = rest || "";
  const [poi, setPoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!poiSlug) return;
    axios.get(`${API}/api/geo/places/detail/${poiSlug}`)
      .then((r) => alive && setPoi(r.data))
      .catch(() => alive && setPoi(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [poiSlug]);

  const meta = poi ? (CAT_ICONS[poi.category] || { Icon: Building2, tint: "#334155", label: "Place" })
                    : { Icon: Building2, tint: "#334155", label: "Place" };

  // Fallback to AI Topic when no Wikipedia summary exists — same
  // engine that powers Weather + Location topic pages. Called at the
  // top level so React hook order stays stable across renders.
  const ai = useAiTopic({
    source:    "poi",
    entityKey: poi?.slug || "",
    title:     poi ? `${poi.name} · ${meta.label} in ${poi.city_name}` : "",
    context:   poi ? `${meta.label} named "${poi.name}" located in ${poi.city_name}${poi.address ? ` (${poi.address})` : ""}. Describe what it is, who it serves, notable features, and community relevance. Keep the tone neutral and factual.` : null,
    deepLink:  poi ? `/places/${poi.city_slug}/${poi.category}/${poi.slug}` : null,
    enabled:   !!poi && !poi.wikipedia_summary,
  });

  if (loading) {
    return (
      <div className="my-16 text-center" data-testid="poi-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }
  if (!poi) {
    return (
      <div className="my-16 text-center text-sm opacity-70" data-testid="poi-detail-empty">
        Place not found. It may have been merged or removed.
      </div>
    );
  }

  const cityHref = `/places/${poi.city_slug}`;

  return (
    <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10" data-testid="poi-detail">
      <nav className="text-xs opacity-70 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <Link to={cityHref} className="hover:underline">{poi.city_name}</Link>
        <span className="mx-2">/</span>
        <Link to={`${cityHref}/${poi.category}`} className="hover:underline">{meta.label}s</Link>
        <span className="mx-2">/</span>
        <span>{poi.name}</span>
      </nav>

      <header className="rounded-2xl overflow-hidden border mb-8" style={{ borderColor: "var(--wofy-border)" }}>
        {/* Whole coloured header is a link back to the category
            directory for this city (e.g. all Hospitals in Portland). */}
        <Link
          to={`/places/${poi.city_slug}/${poi.category}`}
          className="block group"
          data-testid="poi-hero-link"
          aria-label={`Back to all ${meta.label}s in ${poi.city_name}`}
        >
          <div className="relative h-56 sm:h-72 flex items-center justify-center transition-transform group-hover:-translate-y-0.5" style={{ background: `linear-gradient(135deg, ${meta.tint}22, ${meta.tint}44)` }}>
            {poi.wikipedia_image ? (
              <img src={poi.wikipedia_image} alt={poi.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <meta.Icon className="w-20 h-20 opacity-70" style={{ color: meta.tint }} />
            )}
          </div>
          <div className="p-6" style={{ background: "var(--wofy-bg)" }}>
            <div className="text-xs font-mono uppercase tracking-widest opacity-60" style={{ color: meta.tint }}>
              {meta.label}
            </div>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl tracking-tight group-hover:underline decoration-2 underline-offset-4" data-testid="poi-name">{poi.name}</h1>
            {poi.wikipedia_summary && (
              <p className="mt-3 text-sm opacity-85 max-w-3xl leading-relaxed" data-testid="poi-summary">{poi.wikipedia_summary}</p>
            )}
          </div>
        </Link>
      </header>

      {!poi.wikipedia_summary && (
        <section className="mb-8" data-testid="poi-ai-topic">
          <AiTopicContent doc={ai.doc} status={ai.status} retry={ai.retry} title={poi.name} />
        </section>
      )}

      <section className="grid md:grid-cols-3 gap-6 mb-10">
        <div className="md:col-span-2 space-y-3">
          <Fact icon={MapPin}       label="Address"       value={poi.address} />
          <Fact icon={Globe}        label="Website"       value={poi.website} href={poi.website} />
          <Fact icon={Phone}        label="Phone"         value={poi.phone}   href={poi.phone ? `tel:${poi.phone}` : null} />
          <Fact icon={Mail}         label="Email"         value={poi.email}   href={poi.email ? `mailto:${poi.email}` : null} />
          <Fact icon={Clock}        label="Opening hours" value={poi.opening_hours} />
          <Fact icon={Building2}    label="Operator"      value={poi.operator} />
          <Fact icon={Accessibility} label="Accessibility" value={poi.wheelchair} />
          {poi.wikipedia_url && (
            <p className="text-xs opacity-70">
              Source: <a href={poi.wikipedia_url} target="_blank" rel="noreferrer" className="underline">Wikipedia</a>
              {poi.wikidata_id && (<>{" · "}<a href={`https://www.wikidata.org/wiki/${poi.wikidata_id}`} target="_blank" rel="noreferrer" className="underline">Wikidata</a></>)}
            </p>
          )}
        </div>
        <aside className="rounded-xl border p-4 text-sm space-y-3" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
          <div className="font-semibold">Help keep this fresh</div>
          <p className="text-xs opacity-80">
            Spotted a closure, relocation, or wrong detail? Neighbours count on you.
          </p>
          <button
            onClick={() => setReportOpen(true)}
            data-testid="poi-report-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white transition"
            style={{ background: meta.tint }}
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Report an issue
          </button>
          {poi.closure_reports > 0 && (
            <p className="text-xs opacity-70" data-testid="poi-report-count">
              {poi.closure_reports} community report{poi.closure_reports === 1 ? "" : "s"} pending review.
            </p>
          )}
        </aside>
      </section>

      {reportOpen && (
        <ReportDialog poi={poi} onClose={() => setReportOpen(false)} onDone={() => setReportOpen(false)} />
      )}

      <section data-testid="poi-discussion">
        <h2 className="font-display text-2xl mb-3">Discussion</h2>
        <EntityMessageBoard entityType="poi" entityId={poi.slug} title={`Chat about ${poi.name}`} />
      </section>
    </main>
  );
}

function Fact({ icon: Icon, label, value, href }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 mt-1 opacity-60 shrink-0" />
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-50">{label}</div>
        {href
          ? <a href={href} target="_blank" rel="noreferrer" className="hover:underline break-all">{value}</a>
          : <div>{value}</div>}
      </div>
    </div>
  );
}

function ReportDialog({ poi, onClose, onDone }) {
  const { user } = useAuth() || {};
  const [kind, setKind] = useState("closed");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please add a short reason so a local guide can verify.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/geo/places/${poi.slug}/report-closure`, { kind, reason });
      toast.success("Thanks — a local guide will verify shortly.");
      onDone();
    } catch (e) {
      toast.error("Report failed — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="poi-report-dialog"
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-lg">Report an issue</h3>
        <p className="text-xs opacity-70 mt-1">Your report goes to the local guide for verification.</p>
        <label className="block mt-4 text-xs font-mono uppercase tracking-widest opacity-60">Issue type</label>
        <select
          className="mt-1 w-full rounded border px-3 py-2 text-sm"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="poi-report-kind"
        >
          <option value="closed">Permanently closed</option>
          <option value="relocated">Moved / relocated</option>
          <option value="inaccurate">Detail is wrong</option>
        </select>
        <label className="block mt-4 text-xs font-mono uppercase tracking-widest opacity-60">Details</label>
        <textarea
          className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[6em]"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What's changed? Include a date and source if you can."
          data-testid="poi-report-reason"
        />
        <div className="mt-4 flex items-center justify-end gap-3">
          <button className="text-sm opacity-70 hover:opacity-100" onClick={onClose}>Cancel</button>
          <button
            className="rounded-full px-4 py-2 text-sm font-semibold text-white"
            style={{ background: "var(--wofy-primary)" }}
            onClick={submit}
            disabled={busy}
            data-testid="poi-report-submit"
          >
            {busy ? "Sending…" : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
}
